import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAppContext } from "../../../App";
import { dashboardService } from "../../../services/dashboard";

const ALL_VALUE = "$__all";
// Rollback inmediato: false conserva todas las solicitudes individuales.
const USE_BATCH_VARIABLES = true;
const STORAGE_PREFIX = "admincce.dashboard.filters";
const isSecurityVariable = (variable) => variable?.nombre === "grafana_auth";

const sameValue = (a, b) => String(a) === String(b);

const containsValue = (current, value) => {
  if (Array.isArray(current)) {
    return current.some((item) => sameValue(item, value));
  }

  return current !== null && current !== undefined && sameValue(current, value);
};

const initialValuesFrom = (variables) =>
  variables.filter((variable) => !isSecurityVariable(variable)).reduce(
    (result, variable) => ({
      ...result,
      [variable.nombre]:
        variable.kind === "ConstantVariable"
          ? variable.constante
          : variable.valor_inicial,
    }),
    {}
  );

const storageKeyFor = (source) => {
  const identity = source?.uid || source?.slug || source?.id;
  return identity ? `${STORAGE_PREFIX}.${identity}` : null;
};

const loadStoredValues = (source, variables) => {
  const key = storageKeyFor(source);

  if (!key) return {};

  try {
    const stored = JSON.parse(localStorage.getItem(key) || "{}");
    const allowed = new Set(
      variables
        .filter((variable) => !variable.oculto && variable.kind !== "ConstantVariable" && !isSecurityVariable(variable))
        .map((variable) => variable.nombre)
    );

    return Object.keys(stored).reduce(
      (result, name) =>
        allowed.has(name) ? { ...result, [name]: stored[name] } : result,
      {}
    );
  } catch {
    return {};
  }
};

const persistValues = (source, variables, values) => {
  const key = storageKeyFor(source);

  if (!key) return;

  try {
    const persistent = variables.reduce((result, variable) => {
      if (variable.oculto || variable.kind === "ConstantVariable" || isSecurityVariable(variable)) {
        return result;
      }

      if (!Object.prototype.hasOwnProperty.call(values, variable.nombre)) {
        return result;
      }

      return {
        ...result,
        [variable.nombre]: values[variable.nombre],
      };
    }, {});

    localStorage.setItem(key, JSON.stringify(persistent));
  } catch {
    // localStorage puede no estar disponible; el dashboard sigue funcionando.
  }
};

const normalizeInputValue = (variable, value) => {
  if (
    variable?.multi &&
    variable?.incluye_todos &&
    Array.isArray(value) &&
    value.length === 0
  ) {
    return [ALL_VALUE];
  }

  return value;
};

const descendantsOf = (name, variablesByName) => {
  const found = new Set();
  const queue = [...(variablesByName[name]?.hijos || [])];

  while (queue.length) {
    const current = queue.shift();

    if (found.has(current)) continue;

    found.add(current);
    queue.push(...(variablesByName[current]?.hijos || []));
  }

  return found;
};

const normalizeSelection = (variable, current, options) => {
  if (variable.kind === "TextBoxVariable") return current ?? variable.valor_inicial ?? "";
  if (variable.kind === "ConstantVariable") {
    return variable.constante;
  }

  if (variable.incluye_todos && variable.multi && containsValue(current, ALL_VALUE)) {
    return [ALL_VALUE];
  }

  const available = options.map((option) => option.__value);

  if (variable.multi) {
    const currentValues = Array.isArray(current)
      ? current
      : current === null || current === undefined
        ? []
        : [current];

    const valid = currentValues.filter((value) =>
      available.some((candidate) => sameValue(candidate, value))
    );

    if (valid.length) return valid;
    if (variable.incluye_todos) return [ALL_VALUE];

    return available.length ? [available[0]] : [];
  }

  if (available.some((candidate) => sameValue(candidate, current))) {
    return current;
  }

  return available.length ? available[0] : null;
};

export const useDashboard = (dashboardId) => {
  const { auth } = useAppContext();
  const [layout, setLayout] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [unavailable, setUnavailable] = useState(false);
  const [definition, setDefinition] = useState(null);
  const [workingValues, setWorkingValues] = useState({});
  const [committedValues, setCommittedValues] = useState({});
  const [options, setOptions] = useState({});
  const [loadingVariables, setLoadingVariables] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [grafanaAuth, setGrafanaAuth] = useState(null);
  const generationRef = useRef(0);
  const controllerRef = useRef(null);

  const variables = definition?.variables || [];

  const variablesByName = useMemo(
    () =>
      variables.reduce(
        (result, variable) => ({ ...result, [variable.nombre]: variable }),
        {}
      ),
    [variables]
  );

  const resolveVariable = useCallback(
    async (snapshotId, variable, values, signal) => {
      if (variable.kind === "TextBoxVariable") {
        const value = variable.valor_inicial ?? "";
        return [{ __value: value, __text: String(value) }];
      }
      if (variable.kind === "ConstantVariable") {
        return [{
          __value: variable.constante,
          __text: String(variable.constante ?? ""),
        }];
      }

      const response = await dashboardService.getVariableOptions(
        snapshotId,
        variable.nombre,
        values,
        { token: auth, signal }
      );

      return response?.options || [];
    },
    [auth]
  );

  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current = controller;
    const generation = ++generationRef.current;

    const load = async () => {
      setLoading(true);
      setError(null);
      setUnavailable(false);
      setGrafanaAuth(null);

      try {
        const layoutResponse = await dashboardService.getLayout(dashboardId, {
          token: auth,
          signal: controller.signal,
        });

        const snapshotId = layoutResponse.id;
        const variablesResponse = await dashboardService.getVariables(snapshotId, {
          token: auth,
          signal: controller.signal,
        });

        const compiled = variablesResponse.variables;
        const list = (compiled?.variables || []).filter((variable) => !isSecurityVariable(variable));
        let values = {
          ...initialValuesFrom(list),
          ...loadStoredValues(layoutResponse, list),
        };
        const nextOptions = {};

        if (USE_BATCH_VARIABLES && list.length) {
          const response = await dashboardService.getVariableOptionsBatch(
            snapshotId,
            list.map((variable) => variable.nombre),
            values,
            { token: auth, signal: controller.signal }
          );
          const entries = response?.variables ||
            (response?.variable ? {
              [response.variable]: { options: response.options, value: response.value },
            } : {});
          for (const variable of list) {
            if (!entries[variable.nombre]) throw new Error(
              `Faltan opciones para ${variable.nombre}`
            );
            nextOptions[variable.nombre] = entries[variable.nombre].options || [];
            values[variable.nombre] = entries[variable.nombre].value;
          }
        } else {
          for (const level of compiled?.niveles_ejecucion || []) {
            const levelVariables = level
              .map((name) => list.find((variable) => variable.nombre === name))
              .filter(Boolean);
  
            const levelResults = await Promise.all(
              levelVariables.map(async (variable) => [
                variable.nombre,
                await resolveVariable(
                  snapshotId,
                  variable,
                  values,
                  controller.signal
                ),
              ])
            );
  
            levelResults.forEach(([name, variableOptions]) => {
              nextOptions[name] = variableOptions;
              const variable = list.find((item) => item.nombre === name);
              values = {
                ...values,
                [name]: normalizeSelection(
                  variable,
                  values[name],
                  variableOptions
                ),
              };
            });
          }
          }

        // The first iframe must not render until the API issues its signed token.
        const authorization = await dashboardService.getGrafanaAuth(dashboardId, {
          token: auth,
          signal: controller.signal,
        });
        if (!authorization?.grafana_auth || Number(authorization?.grafana_auth_expira) <= Date.now() / 1000) {
          throw new Error('No se recibió autorización vigente para los paneles de Grafana.');
        }
        if (Number(authorization.version) !== Number(layoutResponse.version)) {
          if (generation === generationRef.current) setReloadKey(key => key + 1);
          return;
        }
        if (generation !== generationRef.current) return;

        setGrafanaAuth({
          token: authorization.grafana_auth,
          expires: Number(authorization.grafana_auth_expira),
        });
        setLayout(layoutResponse);
        setDefinition(compiled);
        setOptions(nextOptions);
        setWorkingValues(values);
        setCommittedValues(values);
      } catch (reason) {
        if (reason?.name !== "AbortError" && generation === generationRef.current) {
          if (reason?.status === 404) setUnavailable(true);
          else setError(reason?.message || "No se pudo cargar el dashboard.");
        }
      } finally {
        if (generation === generationRef.current) {
          setLoading(false);
        }
      }
    };

    load();

    return () => controller.abort();
  }, [auth, resolveVariable, dashboardId, reloadKey]);

  // Comprueba el snapshot publicado sin cambiar el token de los iframes.
  const checkVersion = useCallback(async () => {
    if (!dashboardId || !layout?.version) return false;
    try {
      const published = await dashboardService.getVersion(dashboardId, { token: auth });
      if (Number(published?.version) !== Number(layout.version)) {
        setReloadKey((key) => key + 1);
        return true;
      }
    } catch (e) {
      if (e?.status === 404) {
        setUnavailable(true);
        setLayout(null);
        setGrafanaAuth(null);
      } else {
        console.warn("No se pudo comprobar la versión del dashboard", e);
      }
    }
    return false;
  }, [dashboardId, layout?.version, auth]);

  // Renueva credenciales independientemente de la navegación entre pestañas.
  const renewGrafanaAuth = useCallback(async () => {
    if (!dashboardId || !layout?.version) return;
    try {
      const authorization = await dashboardService.getGrafanaAuth(dashboardId, { token: auth });
      if (!authorization?.grafana_auth ||
          Number(authorization?.grafana_auth_expira) <= Date.now() / 1000) {
        throw new Error("No se recibió autorización vigente para Grafana.");
      }
      if (Number(authorization.version) !== Number(layout.version)) {
        setReloadKey((key) => key + 1);
        return;
      }
      setGrafanaAuth({
        token: authorization.grafana_auth,
        expires: Number(authorization.grafana_auth_expira),
      });
    } catch (e) {
      if (e?.status === 404) {
        setUnavailable(true);
        setLayout(null);
        setGrafanaAuth(null);
      } else {
        console.warn("No se pudo renovar la autorización de Grafana", e);
        setGrafanaAuth(null);
      }
    }
  }, [dashboardId, layout?.version, auth]);

  // Se renueva 30 segundos antes del vencimiento, incluso sin interacción.
  useEffect(() => {
    if (!layout || !grafanaAuth?.expires) return;
    const delay = Math.max(1000, (grafanaAuth.expires * 1000) - Date.now() - 30000);
    const timer = setTimeout(() => { renewGrafanaAuth(); }, delay);
    return () => clearTimeout(timer);
  }, [layout, grafanaAuth?.expires, renewGrafanaAuth]);

  const setValue = useCallback(
    async (name, value) => {
      if (!layout?.id || !definition || !variablesByName[name]) return;
      if (isSecurityVariable(variablesByName[name])) return;
    if (await checkVersion()) {
        persistValues(layout, variables, { ...workingValues, [name]: value });
        return;
      }

      controllerRef.current?.abort();
      const controller = new AbortController();
      controllerRef.current = controller;
      const generation = ++generationRef.current;

      const normalizedValue = normalizeInputValue(variablesByName[name], value);
      let values = { ...workingValues, [name]: normalizedValue };
      const affected = descendantsOf(name, variablesByName);
    affected.delete("grafana_auth");
      const nextOptions = { ...options };

      setWorkingValues(values);
      setError(null);

      const loadingNames = {};
      affected.forEach((variableName) => {
        loadingNames[variableName] = true;
      });
      setLoadingVariables(loadingNames);

      try {
        if (USE_BATCH_VARIABLES && affected.size) {
          const response = await dashboardService.getVariableOptionsBatch(
            layout.id,
            Array.from(affected),
            values,
            { token: auth, signal: controller.signal }
          );
          if (generation !== generationRef.current) return;
          const entries = response?.variables ||
            (response?.variable ? {
              [response.variable]: { options: response.options, value: response.value },
            } : {});
          for (const variableName of affected) {
            if (!entries[variableName]) throw new Error(
              `Faltan opciones para ${variableName}`
            );
            nextOptions[variableName] = entries[variableName].options || [];
            values[variableName] = entries[variableName].value;
          }
        } else {
          for (const level of definition.niveles_ejecucion || []) {
            const names = level.filter((variableName) => affected.has(variableName));
  
            if (!names.length) continue;
  
            const results = await Promise.all(
              names.map(async (variableName) => {
                const variable = variablesByName[variableName];
  
                return [
                  variableName,
                  await resolveVariable(
                    layout.id,
                    variable,
                    values,
                    controller.signal
                  ),
                ];
              })
            );
  
            if (generation !== generationRef.current) return;
  
            results.forEach(([variableName, variableOptions]) => {
              nextOptions[variableName] = variableOptions;
              const variable = variablesByName[variableName];
  
              values = {
                ...values,
                [variableName]: normalizeSelection(
                  variable,
                  values[variableName],
                  variableOptions
                ),
              };
            });
  
            setWorkingValues({ ...values });
          }
          }

        if (generation !== generationRef.current) return;

        setOptions(nextOptions);
        setWorkingValues(values);
        setCommittedValues(values);
      } catch (reason) {
        if (reason?.name !== "AbortError" && generation === generationRef.current) {
          setError(reason?.message || "No se pudieron actualizar los filtros.");
        }
      } finally {
        if (generation === generationRef.current) {
          setLoadingVariables({});
        }
      }
    },
    [
      definition,
      layout,
      options,
      resolveVariable,
      variablesByName,
      workingValues,
      checkVersion,
      variables,
    ]
  );

  const exportPanel = useCallback(
    async (panelId) => {
      if (!layout?.id || !panelId) return;

      try {
        const file = await dashboardService.exportPanel(
          layout.id,
          panelId,
          committedValues,
          { token: auth }
        );

        const url = URL.createObjectURL(file.blob);
        const anchor = document.createElement("a");

        anchor.href = url;
        anchor.download = file.filename;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        URL.revokeObjectURL(url);
      } catch (reason) {
        setError(reason?.message || "No se pudo exportar el panel.");
        throw reason;
      }
    },
    [auth, committedValues, layout]
  );

  useEffect(() => {
    if (!layout || !variables.length || !Object.keys(committedValues).length) {
      return;
    }

    persistValues(layout, variables, committedValues);
  }, [committedValues, layout, variables]);

  return {
    id: layout?.id || null,
    layout: layout?.layout || null,
    source: layout,
    variables,
    values: workingValues,
    committedValues,
    grafanaAuth: grafanaAuth?.token || null,
    options,
    loadingVariables,
    loading,
    error,
    unavailable,
    checkVersion,
    setValue,
    exportPanel,
  };
};
